import axios from 'axios'
import { configure as configureCastle } from '@castleio/castle-js'
import { CURRENCY } from '@common/constants'
import {
    BuyBillingInfoDataType,
    BuyPaymentDataType,
    BuySubmitDataType,
    BuyCalculatePriceDataType,
    BuySubmitWithCardAddDataType,
} from '@buy/state/checkout/state'
import {
    BuyUserDataType,
    CreateAccountRequest,
    BuyAbandonedCartPayload,
    CreateAccountResponse,
    CreateCartRequest,
    CreateCartResponse,
    SetupTransactionRequest,
    SetupTransactionResponse,
    InvoiceHistoryResponse,
    ApplyPromoCodeRequest,
    PriceInfoPayload,
} from '@buy/state/user/state'
import { ValidateUserRequestBody, ValidateUserResponseBody } from '@buy/state/validate-user/state'
import { GetAddressResponseBody } from '@buy/state/get-address/state'
import { UpdateBillingAddressRequest } from '@buy/state/update-billing-address/state'
import { SetTaxDetailsRequest } from '@buy/state/set-tax-details/state'
import { getContentLanguage } from '@buy/utils/language-helpers'
import { PlanResponseData } from '@buy/state/plan-response.data'
import { TaxDetailsRequestUCS } from '@buy/state/validate-tax-number/state'
import { ValidateAddressRequestUCS, ValidateAddressResponseUCS } from '@common/state/location/state'

const castle = configureCastle({ pk: process.env.CASTLE_PUBLISHABLE_KEY })

export interface BuyPaymentMethodDataType {
    accessToken?: string
    billingAccountKey?: string
}

export interface BuyCardHeadersType {
    AccessToken: string
    BillingAccountKey: string
    PaymentMethodKey: string
}

export interface BuyLabelDataType {
    id: string
    plan: string
    promoCode: string
    accessToken: string
    timezone: string
}

export interface apiQueryParamsType {
    /* eslint-disable camelcase */
    sc_site: string
    sc_lang?: string
}

export const apiQueryParams = {
    // TODO: load based on product
    sc_lang: 'en',
}

export const setLanguageParam = (queryParams: apiQueryParamsType) => {
    return {
        ...queryParams,
        sc_lang: getContentLanguage(),
    }
}

/* APP DATA */

// get buy flow api data
export function getBuyPlanData(contentEndpoint: string, plan: string) {
    const params = {
        sc_lang: getContentLanguage(),
        plan,
    }
    return axios.get<PlanResponseData>(`/ecommapi/content/purchase/${contentEndpoint}`, { params })
}
// get available payment  methods
export function getAvailablePaymentMethods() {
    return axios.post('/ecommapi/purchase/GetAvailablePaymentMethods', null, {
        withCredentials: true,
    })
}

// create user account
export function createUserAccount(data: BuyUserDataType): Promise<any> {
    return axios.post('/ecommapi/identity/CreateAccount/', data, {
        withCredentials: true,
        params: { ...apiQueryParams },
    })
}

// create user account UCS
export async function createUserAccountUcs(data: CreateAccountRequest, fingerPrintSessionId: string) {
    const castleRequestToken = await castle.createRequestToken()

    // https://confluence.ops.expertcity.com/display/COM/Create+Account
    const response = await axios.post<CreateAccountResponse>(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/accounts`,
        data,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                castle_request_token: castleRequestToken,
                fingerPrintSessionId,
            },
        }
    )
    return response
}

// vadliate address UCS
export async function validateAddressUCS(data: ValidateAddressRequestUCS) {
    const castleRequestToken = await castle.createRequestToken()

    // https://confluence.ops.expertcity.com/spaces/COM/pages/776798285/Validate+Address+Net+New+Customer
    return axios.post<ValidateAddressResponseUCS>(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/billing/address/validate`,
        data,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                castle_request_token: castleRequestToken,
            },
        }
    )
}

// validate user UCS
export async function validateUserUCS(userToken: string, data: ValidateUserRequestBody) {
    // https://confluence.ops.expertcity.com/spaces/COM/pages/783450386/Validate+User+Net+New+Customer
    const response = await axios.post<ValidateUserResponseBody>(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/validateUser`,
        data,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                Authorization: `Bearer ${userToken}`,
            },
        }
    )
    return response
}

// get address UCS
export async function getAddressUCS(userToken: string, accountKey: string) {
    // https://confluence.ops.expertcity.com/spaces/COM/pages/249049376/Get+address
    const response = await axios.get<GetAddressResponseBody>(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/billing/${accountKey}/address`,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                Authorization: `Bearer ${userToken}`,
            },
        }
    )
    return response
}

// get tax details UCS
export async function getTaxDetailsUCS(userToken: string, accountKey: string) {
    // https://confluence.ops.expertcity.com/spaces/COM/pages/394543535/Get+tax+details
    const response = await axios.get<ValidateUserResponseBody>(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/billing/${accountKey}/taxdetails`,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                Authorization: `Bearer ${userToken}`,
            },
        }
    )
    return response
}

export function createCartV3(userToken: string, accountKey: string, data: CreateCartRequest) {
    // https://confluence.ops.expertcity.com/display/COM/PUT+Cart
    return axios.put<CreateCartResponse>(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v3/cart/${accountKey}`,
        data,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                Authorization: `Bearer ${userToken}`,
            },
        }
    )
}

export function createCart(userToken: string, accountKey: string, data: CreateCartRequest) {
    // https://confluence.ops.expertcity.com/display/COM/PUT+Cart
    return axios.put<CreateCartResponse>(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/cart/${accountKey}`,
        data,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                Authorization: `Bearer ${userToken}`,
            },
        }
    )
}

export function deleteCart(userToken: string, accountKey: string) {
    // https://confluence.ops.expertcity.com/spaces/COM/pages/318572738/DELETE+CART
    return axios.delete<boolean>(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/cart/${accountKey}`,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                Authorization: `Bearer ${userToken}`,
            },
            validateStatus: () => true,
        }
    )
}

export function applyPromoCode(userToken: string, accountKey: string, data: ApplyPromoCodeRequest) {
    // https://confluence.ops.expertcity.com/spaces/COM/pages/371599795/POST+validate+Apply+PromoCode
    return axios.post(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/cart/${accountKey}/promocode`,
        data,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                Authorization: `Bearer ${userToken}`,
            },
        }
    )
}

export function setupTransaction(userToken: string, accountKey: string, data: SetupTransactionRequest) {
    // https://confluence.ops.expertcity.com/display/COM/Setup+Transactions+-+COPAS
    return axios.post<SetupTransactionResponse>(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/transaction/${accountKey}/setup`,
        data,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                Authorization: `Bearer ${userToken}`,
            },
        }
    )
}

export function getInvoiceHistory(userToken: string, accountKey: string) {
    // https://confluence.ops.expertcity.com/display/COM/Get+invoice+history
    return axios.get<InvoiceHistoryResponse>(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/billing/${accountKey}/invoices`,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                Authorization: `Bearer ${userToken}`,
            },
        }
    )
}

export function fetchInvoiceAsBlob(userToken: string, accountKey: string, invoiceId: string) {
    // https://confluence.ops.expertcity.com/display/COM/Download+invoice
    return axios.get(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/billing/${accountKey}/invoice/${invoiceId}`,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                Authorization: `Bearer ${userToken}`,
            },
            responseType: 'blob',
        }
    )
}

// create billing information
export function createBillingInfo(data: BuyBillingInfoDataType): Promise<any> {
    return axios.post('/ecommapi/purchase/SetBillingInfo', data, {
        withCredentials: true,
    })
}

// update billing address UCS
export function updateBillingAddressUCS(
    userToken: string,
    accountKey: string,
    data: UpdateBillingAddressRequest
): Promise<any> {
    // confluence.ops.expertcity.com/spaces/COM/pages/249056017/Update+billing+address
    return axios.put(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/billing/${accountKey}/address`,
        data,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                Authorization: `Bearer ${userToken}`,
            },
        }
    )
}

// set tax details UCS
export function updateTaxDetailsUCS(userToken: string, accountKey: string, data: SetTaxDetailsRequest): Promise<any> {
    // https://confluence.ops.expertcity.com/spaces/COM/pages/394543548/POST+Tax+Details
    return axios.put(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/billing/${accountKey}/taxdetails`,
        data,
        {
            headers: {
                tenant: process.env.LOGGING_TENANT,
                Authorization: `Bearer ${userToken}`,
            },
        }
    )
}

// calculate payment
export function calculatePrice(data: BuyCalculatePriceDataType): Promise<any> {
    return axios.post('/ecommapi/purchase/CalculatePrice', data)
}

// create payment info
export function savePayment(data: BuyPaymentDataType) {
    return axios.post('/ecommapi/purchase/SavePaymentMethodV3', data, {
        withCredentials: true,
    })
}

// submit payment with card add
// api reference: https://confluence.ops.expertcity.com/display/MMM/API+-+Public+-+Process+Purchase+With+Card+Add+V3
export function submitPaymentWithCardAdd(data: BuySubmitWithCardAddDataType): Promise<any> {
    return axios.post('/ecommapi/purchase/ProcessPurchaseWithCardAddV3', data, {
        withCredentials: true,
    })
}

// submit payment
export function submitPayment(data: BuySubmitDataType | BuySubmitWithCardAddDataType) {
    return axios.post('/ecommapi/purchase/ProcessPurchaseV3', data, {
        withCredentials: true,
    })
}

export async function validateTaxNumber(country: string, taxNumber: string, federalTaxType?: string) {
    const castleRequestToken = await castle.createRequestToken()

    // https://confluence.ops.expertcity.com/spaces/COM/pages/776538035/Validate+TaxDetails+Net+New+Customer
    const data: TaxDetailsRequestUCS = {
        countryCode: country,
        taxNumber,
        subTaxNumberType: federalTaxType,
    }
    return axios.post(`${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/billing/taxdetails`, data, {
        headers: {
            tenant: process.env.LOGGING_TENANT,
            castle_request_token: castleRequestToken,
        },
    })
}

// cart abandonment
export function submitAbandonedCart(data: BuyAbandonedCartPayload) {
    return axios.post(`${process.env.AWS_ABANDON_ENDPOINT}`, data)
}

export type CurrencyRestrictionsResponseData = CURRENCY[]
export async function getCurrencyRestrictions(country: string) {
    const castleRequestToken = await castle.createRequestToken()

    // https://confluence.ops.expertcity.com/spaces/COM/pages/607977888/Currency+Restrictions
    const headers = {
        castle_request_token: castleRequestToken,
        tenant: process.env.LOGGING_TENANT,
    }
    return axios.get<CurrencyRestrictionsResponseData>(
        `${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/currencyRestrictions/${country}`,
        {
            headers,
        }
    )
}

export async function getPricingInfoData(data: PriceInfoPayload) {
    const castleRequestToken = await castle.createRequestToken()

    // https://confluence.ops.expertcity.com/spaces/COM/pages/527302860/Price+Info+API
    const headers = {
        castle_request_token: castleRequestToken,
        tenant: process.env.LOGGING_TENANT,
    }
    const params = {
        ...data,
    }
    return axios.get(`${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/priceInfo`, {
        headers,
        params,
    })
}

export async function getCouponInfo(couponCode: string) {
    const castleRequestToken = await castle.createRequestToken()

    // https://confluence.ops.expertcity.com/spaces/COM/pages/598835247/Coupon+Info+API
    const headers = {
        castle_request_token: castleRequestToken,
        tenant: process.env.LOGGING_TENANT,
    }
    return axios.get(`${process.env.UCS_SERVICE_ENDPOINT}/unifiedcommerceservice/rest/v2/couponInfo/${couponCode}`, {
        headers,
    })
}

export default {
    getBuyPlanData,
    calculatePrice,
    createUserAccount,
    createUserAccountUcs,
    createBillingInfo,
    submitPaymentWithCardAdd,
    getPricingInfoData,
    validateAddressUCS,
}
